import { useMemo } from "react";
import type { RefObject } from "react";
import type { CompTopicExampleRow } from "@nexus/contracts";
import { Button, Skeleton } from "@nexus/ui";
import { useTranslation } from "react-i18next";
import { formatCompactNumber } from "../../lib/formatCompactNumber.js";
import { humanizeBrandName } from "../../lib/humanizeBrandName.js";
import { useInfiniteScrollSentinel } from "../../hooks/useInfiniteScrollSentinel.js";
import { ExampleCreativeGrid, type ExampleCardVm } from "../shared/ExampleCreativeGrid.js";

interface CompTopicDetailExamplesTabProps {
  examples: CompTopicExampleRow[];
  isLoading: boolean;
  isError: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isFetchNextPageError: boolean;
  onFetchNextPage: () => void;
  /** Drawer scroll container the creatives grid virtualizes against. */
  scrollContainerRef?: RefObject<HTMLElement | null>;
}

const SKELETON_CARDS = 8;

function formatDaysPerAsset(row: CompTopicExampleRow): string {
  if (row.daysPerAsset != null) return formatCompactNumber(row.daysPerAsset);
  if (row.assets <= 0) return "—";
  const ratio = row.days / row.assets;
  return Number.isFinite(ratio) ? formatCompactNumber(ratio) : "—";
}

function ExamplesSkeleton() {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
      {Array.from({ length: SKELETON_CARDS }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-md border">
          <Skeleton className="aspect-[4/5] rounded-none" />
          <div className="px-2.5 py-2">
            <Skeleton className="h-3.5 w-2/3" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function CompTopicDetailExamplesTab({
  examples,
  isLoading,
  isError,
  hasNextPage,
  isFetchingNextPage,
  isFetchNextPageError,
  onFetchNextPage,
  scrollContainerRef,
}: CompTopicDetailExamplesTabProps) {
  const { t } = useTranslation();

  const sentinelRef = useInfiniteScrollSentinel({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage: onFetchNextPage,
    isError: isFetchNextPageError,
  });

  const cards = useMemo<ExampleCardVm[]>(
    () =>
      examples.map((row) => {
        const isVideo = row.objectType.toLowerCase() === "video";
        return {
          key: row.hash,
          thumbnailUrl: row.thumbnailUrl,
          src: isVideo ? row.creativeUrl : row.creativeUrl || row.thumbnailUrl,
          type: isVideo ? "video" : "image",
          title: row.competitor ? humanizeBrandName(row.competitor) : row.adName,
          meta: t("overview.compTopicDetail.usedInAds", {
            defaultValue: "Used in {{count}} ads",
            count: formatCompactNumber(row.ads),
          }),
          metrics: [
            {
              label: t("overview.compTopicDetail.metricDays", { defaultValue: "Days" }),
              value: formatCompactNumber(row.days),
            },
            {
              label: t("overview.compTopicDetail.metricAssets", { defaultValue: "Assets" }),
              value: formatCompactNumber(row.assets),
            },
            {
              label: t("overview.compTopicDetail.metricDaysAssets", { defaultValue: "Days/Assets" }),
              value: formatDaysPerAsset(row),
            },
          ],
        };
      }),
    [examples, t],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 px-6 py-5">
        <ExamplesSkeleton />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col gap-4 px-6 py-5">
        <p className="text-sm text-destructive">
          {t("overview.compTopicDetail.examplesError", { defaultValue: "Failed to load examples." })}
        </p>
      </div>
    );
  }

  if (examples.length === 0) {
    return (
      <div className="flex flex-col gap-4 px-6 py-5">
        <p className="text-sm text-muted-foreground">
          {t("overview.compTopicDetail.noExamples", { defaultValue: "No creatives match this topic." })}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 px-6 py-5">
      <ExampleCreativeGrid
        cards={cards}
        emptyLabel={t("overview.compTopicDetail.noExamples", { defaultValue: "No creatives match this topic." })}
        scrollContainerRef={scrollContainerRef}
      />

      {hasNextPage && (
        <div
          ref={sentinelRef}
          data-testid="comp-topic-detail-examples-sentinel"
          className="flex items-center justify-center gap-2 py-3 text-xs text-muted-foreground"
        >
          {isFetchNextPageError ? (
            <>
              <span className="text-destructive">
                {t("overview.topicDetail.loadMoreError", { defaultValue: "Couldn’t load more." })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onFetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {t("overview.topicDetail.retry", { defaultValue: "Retry" })}
              </Button>
            </>
          ) : (
            t("overview.topicDetail.loadingMore", { defaultValue: "Loading more…" })
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
        <p className="text-xs text-muted-foreground">
          {t("overview.topicDetail.loadedCount", { defaultValue: "{{count}} loaded", count: examples.length })}
        </p>
      </div>
    </div>
  );
}
